// Error toast notification component
import React, { useState, useEffect, useCallback } from 'react';
import { AppError } from '../../types/errors';
import './ErrorToast.css';

interface ErrorToastProps {
  error: AppError;
  onDismiss: () => void;
  onRetry?: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  className?: string;
}

export const ErrorToast: React.FC<ErrorToastProps> = ({
  error,
  onDismiss,
  onRetry,
  autoHide = true,
  autoHideDelay = 5000,
  position = 'top-right',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss();
    }, 300); // Wait for exit animation
  }, [onDismiss]);

  useEffect(() => {
    if (!autoHide) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, autoHideDelay - elapsed);
      const progressPercent = (remaining / autoHideDelay) * 100;
      
      setProgress(progressPercent);
      
      if (remaining <= 0) {
        handleDismiss();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [autoHide, autoHideDelay, handleDismiss]);



  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    handleDismiss();
  };

  const getToastIcon = () => {
    switch (error.severity) {
      case 'critical':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'medium':
        return '⚡';
      case 'low':
        return 'ℹ️';
      default:
        return '❗';
    }
  };

  const containerClasses = [
    'error-toast',
    `error-toast--${position}`,
    `error-toast--${error.severity}`,
    isVisible ? 'error-toast--visible' : 'error-toast--hidden',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      role="alert"
      aria-live="assertive"
    >
      <div className="error-toast__content">
        {/* Icon */}
        <div className="error-toast__icon" aria-hidden="true">
          {getToastIcon()}
        </div>

        {/* Message */}
        <div className="error-toast__message">
          <div className="error-toast__title">
            {error.category === 'network' ? 'Connection Problem' : 'Error'}
          </div>
          <div className="error-toast__text">
            {error.userMessage}
          </div>
        </div>

        {/* Actions */}
        <div className="error-toast__actions">
          {error.retryable && onRetry && (
            <button
              type="button"
              className="error-toast__action error-toast__action--retry"
              onClick={handleRetry}
              aria-label="Retry action"
            >
              Retry
            </button>
          )}
          
          <button
            type="button"
            className="error-toast__action error-toast__action--dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Progress bar for auto-hide */}
      {autoHide && (
        <div 
          className="error-toast__progress"
          style={{ width: `${progress}%` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// Toast container component for managing multiple toasts
interface ErrorToastContainerProps {
  errors: Array<{ id: string; error: AppError; onRetry?: () => void }>;
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const ErrorToastContainer: React.FC<ErrorToastContainerProps> = ({
  errors,
  onDismiss,
  position = 'top-right',
  maxToasts = 3,
}) => {
  // Limit the number of visible toasts
  const visibleErrors = errors.slice(0, maxToasts);

  if (visibleErrors.length === 0) {
    return null;
  }

  const containerClasses = [
    'error-toast-container',
    `error-toast-container--${position}`,
  ].join(' ');

  return (
    <div className={containerClasses}>
      {visibleErrors.map(({ id, error, onRetry }) => (
        <ErrorToast
          key={id}
          error={error}
          onDismiss={() => onDismiss(id)}
          onRetry={onRetry}
          position={position}
        />
      ))}
      
      {/* Show count if there are more errors */}
      {errors.length > maxToasts && (
        <div className="error-toast-container__overflow">
          +{errors.length - maxToasts} more error{errors.length - maxToasts !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};